import { useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Download, Loader2 } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/motion";
import { PageHero } from "@/pages/ServicesPage";
import { IMAGES } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHAPTERS = [
  ["01", "Reading the Land", "Sun, wind, grade, water — and the views worth framing."],
  ["02", "Phasing an Estate Build", "What to construct first, and why the bones come before the rooms."],
  ["03", "The Investment Conversation", "Honest guidance on luxury budget ranges — from Essential to full Estate."],
  ["04", "Building With the Alberta Seasons", "Chinooks, frost and the planting calendar, planned to your advantage."],
  ["05", "Before You Begin", "Twelve questions worth asking — of yourself, and of anyone you hire."],
];

function GuideCover({ large = false }) {
  return (
    <div className="relative mx-auto max-w-xs">
      <div className="absolute -inset-3 border border-[#C5A059]/40" aria-hidden="true" />
      <div
        data-testid="guide-cover"
        className={`relative -rotate-2 border border-[#E0D8CC] bg-white text-center shadow-[0_24px_60px_rgba(28,32,29,0.14)] transition-transform duration-500 hover:rotate-0 ${large ? "px-10 py-16" : "px-8 py-14"}`}
      >
        <p className="eyebrow">2026 Edition</p>
        <p className="mt-5 font-display text-3xl font-light leading-tight">The Acreage Landscape Planning Guide</p>
        <div className="gold-rule my-6" />
        <p className="text-xs uppercase tracking-[0.2em] text-[#758077]">Estate Outdoors · Calgary</p>
      </div>
    </div>
  );
}

export default function GuidePage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const startedAt = useRef(Date.now());
  const honeypot = useRef("");

  const download = () => {
    const a = document.createElement("a");
    a.href = `${API}/guide.pdf`;
    a.download = "Estate-Outdoors-Calgary-Acreage-Planning-Guide.pdf";
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!consent) {
      setError("Please accept the privacy consent to continue.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/guide-downloads`, {
        full_name: name, email, consent,
        website: honeypot.current, started_at: startedAt.current,
      });
      setDone(true);
      toast.success("Your guide is on its way.");
      download();
    } catch (err) {
      const msg = err.response?.data?.detail || "Something went wrong. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="guide-page">
      <SEO
        title="The Acreage Landscape Planning Guide | Estate Outdoors Calgary"
        description="A complimentary planning guide for estate homeowners: reading your land, phasing an acreage build, luxury landscape investment ranges and building with the Alberta seasons. Springbank, Bearspaw and Calgary."
      />
      <PageHero
        testid="guide-hero"
        eyebrow="Complimentary Guide · 2026 Edition"
        title="The Acreage Landscape Planning Guide"
        subtitle="How to read your land, phase your vision, and plan an investment worthy of the property."
        image={IMAGES.pergolaLawn}
      />

      <section className="py-20 sm:py-28">
        <div className="mx-auto max-w-7xl px-5 sm:px-8 grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <Reveal>
              <GuideCover large />
            </Reveal>
            <Reveal delay={0.15} className="mt-16">
              <p className="eyebrow !text-[#8B5A2B]">Inside the Guide</p>
              <div className="mt-6 divide-y divide-[#E0D8CC] border-y border-[#E0D8CC]">
                {CHAPTERS.map(([num, t, d]) => (
                  <div key={num} data-testid={`guide-chapter-${num}`} className="flex gap-6 py-5">
                    <span className="font-display text-2xl font-light italic text-[#C5A059]">{num}</span>
                    <div>
                      <h2 className="font-display text-xl font-normal">{t}</h2>
                      <p className="mt-1 text-sm leading-relaxed text-[#4A524C]">{d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-6">
            <Reveal delay={0.1} className="lg:sticky lg:top-28">
              {done ? (
                <div data-testid="guide-success" className="border border-[#C5A059]/50 bg-white p-10 sm:p-14 text-center">
                  <CheckCircle2 size={44} strokeWidth={1.2} className="mx-auto text-[#6B7C6E]" aria-hidden="true" />
                  <h2 className="mt-6 font-display text-3xl font-light">Enjoy the Guide.</h2>
                  <p className="mx-auto mt-4 max-w-md text-base leading-relaxed text-[#4A524C]">
                    Your download has begun, and a copy is on its way to your inbox. When you're ready to walk
                    your property together, we'd be delighted to hear from you.
                  </p>
                  <button onClick={download} data-testid="guide-download-again-btn" className="btn-outline-dark mt-8">
                    <Download size={15} /> Download Again
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} data-testid="guide-form" className="border border-[#E0D8CC] bg-white p-7 sm:p-12">
                  <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" className="absolute left-[-9999px] top-0 h-0 w-0 opacity-0" onChange={(e) => { honeypot.current = e.target.value; }} />
                  <h2 className="font-display text-3xl font-light">Where Shall We Send It?</h2>
                  <p className="mt-3 text-sm leading-relaxed text-[#4A524C]">
                    Complimentary for estate homeowners. No spam — only the guide, and a note when the new edition arrives.
                  </p>
                  <div className="mt-8 space-y-6">
                    <div>
                      <label htmlFor="g-name" className="field-label">Full Name *</label>
                      <input id="g-name" data-testid="guide-name-input" required className="field-input" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
                    </div>
                    <div>
                      <label htmlFor="g-email" className="field-label">Email *</label>
                      <input id="g-email" data-testid="guide-email-input" required type="email" className="field-input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                    </div>
                  </div>
                  <label className="mt-7 flex cursor-pointer items-start gap-3 text-sm text-[#4A524C]">
                    <input type="checkbox" data-testid="guide-consent-checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} className="mt-0.5 h-4 w-4 accent-[#8B5A2B]" />
                    <span>I consent to Estate Outdoors Calgary emailing me the guide and storing my details as described in the Privacy Policy. *</span>
                  </label>
                  {error && (
                    <p data-testid="guide-form-error" role="alert" className="mt-6 border border-[#9c3d33]/40 bg-[#9c3d33]/5 px-4 py-3 text-sm text-[#9c3d33]">{error}</p>
                  )}
                  <button type="submit" data-testid="guide-submit-btn" disabled={submitting} className="btn-primary mt-8 w-full disabled:opacity-60">
                    {submitting ? (<><Loader2 size={16} className="animate-spin" /> Preparing…</>) : (<><Download size={16} /> Send Me the Guide</>)}
                  </button>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
